import { useMemo } from 'react';
import { NewSelectMultiSelect, NewSelectMultiSelectOption } from '../PIM/common';
import { BombiixChip, BombiixChipVariant } from '../BombiixChip';

/**
 * Clear ("X") control inside the select. react-select is styled through JS
 * objects rather than the stylesheet, so the removable-chip look from
 * `_chip.scss` ($color-red-bg / $color-red / $radius-base) is repeated here as
 * literals to keep the two close controls identical.
 */
const CLEAR_INDICATOR_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '20px',
  height: '20px',
  padding: 0,
  margin: '0 4px 0 0',
  borderRadius: '4px',
  backgroundColor: '#fee5e6',
  color: '#fa979b',
  cursor: 'pointer',
  '&:hover': { color: '#ef7f83' },
};

/**
 * Normalises react-select's `onChange` value — an array under `isMulti`, one
 * option or null otherwise — onto the array shape callers always receive.
 * @param {unknown} next - The raw react-select selection.
 * @returns {NewSelectMultiSelectOption[]} The selection as a list.
 */
function toSelection(next: unknown): NewSelectMultiSelectOption[] {
  if (Array.isArray(next)) {
    return (next as NewSelectMultiSelectOption[]).slice();
  }
  return next ? [next as NewSelectMultiSelectOption] : [];
}

export interface BombiixChipSelectProps {
  options: ReadonlyArray<NewSelectMultiSelectOption>;
  value: ReadonlyArray<NewSelectMultiSelectOption>;
  onChange: (_selected: NewSelectMultiSelectOption[]) => void;
  /** Accessible name for one chip's remove control, e.g. `(option) => t('REMOVE', { name: option.label })`. */
  removeLabel: (_option: NewSelectMultiSelectOption) => string;
  label?: string;
  required?: boolean;
  invalid?: boolean;
  /** Control text while nothing is picked. */
  placeholder?: string;
  /** Control text when every option is picked. Falls back to the joined list. */
  allSelectedLabel?: string;
  /** Renders the "Select All" row in the menu when passed. */
  selectAllLabel?: string;
  showSearch?: boolean;
  searchPlaceholder?: string;
  chipVariant?: BombiixChipVariant;
  inputId?: string;
  /** Applied to the chip row, for page-level spacing overrides. */
  chipsClassName?: string;
  chipsTestId?: string;
  /**
   * Set false for a single-select field. The control then accepts one pick at a
   * time and `onChange` still fires with an array — of zero or one option — so
   * callers share one shape with the multi-select case.
   */
  isMulti?: boolean;
}

/**
 * BombiixChipSelect — select whose picks render as removable chips BELOW
 * the control rather than inside it. Multi by default; pass `isMulti={false}`
 * for a single-select field that keeps the same chip presentation. The control itself shows a read-only
 * summary of the selection, so a long list never scrolls the pills out of view
 * (the failure mode of react-select's own in-control chips).
 *
 * Selection state is owned by the caller: `onChange` fires for both a menu pick
 * and a chip removal, always with the full next selection.
 */
function BombiixChipSelect({
  options,
  value,
  onChange,
  removeLabel,
  label,
  required,
  invalid,
  placeholder,
  allSelectedLabel,
  selectAllLabel,
  showSearch,
  searchPlaceholder,
  chipVariant = 'purple',
  inputId,
  chipsClassName,
  chipsTestId,
  isMulti = true,
}: BombiixChipSelectProps) {
  const summary = useMemo<string>(() => {
    if (value.length === 0) {
      return placeholder ?? '';
    }
    if (allSelectedLabel && options.length > 0 && value.length === options.length) {
      return allSelectedLabel;
    }
    return value.map((option) => option.label).join(', ');
  }, [value, options.length, placeholder, allSelectedLabel]);

  const chipRowClass = ['bombiix-chip-select__chips', 'mt-1', chipsClassName].filter(Boolean).join(' ');

  /* `exactOptionalPropertyTypes` rejects an explicit `undefined` on an optional
     prop, so anything the caller left out is omitted from the bag rather than
     forwarded as undefined. */
  const passthrough = {
    ...(label !== undefined && { label }),
    ...(required !== undefined && { required }),
    ...(invalid !== undefined && { invalid }),
    ...(selectAllLabel !== undefined && { selectAllLabel }),
    ...(showSearch !== undefined && { showSearch }),
    ...(searchPlaceholder !== undefined && { searchPlaceholder }),
    ...(inputId !== undefined && { inputId }),
  };

  return (
    <>
      <NewSelectMultiSelect
        {...(isMulti ? { isMulti: true } : {})}
        isClearable
        controlShouldRenderValue={false}
        options={options}
        value={isMulti ? value : (value[0] ?? null)}
        placeholder={summary}
        styles={{ clearIndicator: (base) => ({ ...base, ...CLEAR_INDICATOR_STYLE }) }}
        onChange={(next) => onChange(toSelection(next))}
        {...passthrough}
      />
      {value.length > 0 ? (
        <div className={chipRowClass} data-testid={chipsTestId}>
          {value.map((option) => (
            <BombiixChip
              key={String(option.value)}
              variant={chipVariant}
              removeLabel={removeLabel(option)}
              onRemove={() => onChange(value.filter((picked) => picked.value !== option.value))}
            >
              {option.label}
            </BombiixChip>
          ))}
        </div>
      ) : null}
    </>
  );
}

export default BombiixChipSelect;
